import { useState, useEffect, FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Flex, Text, chakra } from '@chakra-ui/react';
import { Input } from '@/components/ui/Input/Input';
import { PasswordValidation } from '@/components/ui/PasswordValidation/PasswordValidation';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/useToast';
import { Toast } from '@/components/ui/Toast/Toast';

const getPasswordErrors = (password: string): string[] => {
  const errors: string[] = [];
  if (password.length < 6) errors.push('At least 6 characters');
  if (!/[A-Z]/.test(password)) errors.push('At least one uppercase letter');
  if (!/[a-z]/.test(password)) errors.push('At least one lowercase letter');
  if (!/\d/.test(password)) errors.push('At least one number');
  if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) errors.push('At least one special character');
  return errors;
};

export const ResetPasswordForm = () => {
  const navigate = useNavigate();
  // Read recovery params via React Router so they resolve under HashRouter,
  // where the query lives inside the `#/reset-password?…` fragment and is not
  // visible to window.location.search.
  const [searchParams] = useSearchParams();
  const tokenHash = searchParams.get('token_hash');
  const hasCode = searchParams.has('code');
  const paramType = searchParams.get('type');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { showToast, hideToast, toast } = useToast();

  useEffect(() => {
    let mounted = true;

    const finishWithError = () => {
      if (!mounted) return;
      setTokenError('Your password reset link has expired or is invalid. Please request a new one.');
      setInitializing(false);
    };

    const ready = () => {
      if (!mounted) return;
      setTokenError(null);
      setInitializing(false);
    };

    // Recovery links can arrive in several shapes depending on the Supabase
    // flow type and email template:
    //   1. PKCE:    /reset-password?code=...            (handled by detectSessionInUrl)
    //   2. OTP:     /reset-password?token_hash=...&type=recovery
    //   3. Implicit:/reset-password#access_token=...&type=recovery
    // (1) and (2) are read via useSearchParams above; (3) is detected by
    // supabase-js via detectSessionInUrl, which then fires PASSWORD_RECOVERY.
    const hasImplicitToken = window.location.hash.includes('access_token');

    // supabase-js fires PASSWORD_RECOVERY once it processes the recovery link
    // (or SIGNED_IN after a PKCE code exchange). Treat either as "ready".
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY' || (event === 'SIGNED_IN' && session)) {
        ready();
      }
    });

    const init = async () => {
      // OTP / token_hash flow needs an explicit verify; it works cross-device
      // (no PKCE code verifier required).
      if (tokenHash) {
        const { error } = await supabase.auth.verifyOtp({ type: 'recovery', token_hash: tokenHash });
        if (!mounted) return;
        if (error) finishWithError();
        else ready();
        return;
      }

      // PKCE / implicit: detectSessionInUrl processes the URL on client init,
      // possibly before this runs. Check for an established session, and if the
      // URL still carries a code, give the async exchange a brief moment.
      const { data: { session } } = await supabase.auth.getSession();
      if (!mounted) return;
      if (session) {
        ready();
        return;
      }

      const hasCredentials = hasCode || hasImplicitToken || paramType === 'recovery';
      if (!hasCredentials) {
        navigate('/forgot-password');
        return;
      }

      // Wait briefly for detectSessionInUrl / PASSWORD_RECOVERY, then re-check.
      setTimeout(async () => {
        const { data: { session: retried } } = await supabase.auth.getSession();
        if (!mounted) return;
        if (retried) ready();
        else finishWithError();
      }, 1500);
    };

    init();

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [navigate, tokenHash, hasCode, paramType]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    const passwordErrors = getPasswordErrors(password);
    if (passwordErrors.length > 0) {
      setError(passwordErrors[0]);
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) throw updateError;

      if ('vibrate' in navigator) navigator.vibrate(50);

      showToast('Your password has been updated successfully.', 'success');
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      let message = (err instanceof Error && err.message) || 'Failed to update password. Please try again.';
      if (message.includes('expired') || message.includes('invalid')) {
        message = 'Your reset link has expired. Please request a new one.';
      }
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  if (initializing) {
    return (
      <Flex w="full" maxW="400px" justify="center" align="center" py="10">
        <Text fontSize="sm" color="volyx.textSecondary">Verifying reset link...</Text>
      </Flex>
    );
  }

  if (tokenError) {
    return (
      <Flex w="full" maxW="400px" direction="column" align="center" gap="4">
        <Box w="full" p="4" bg="rgba(255,59,48,0.1)" color="volyx.error" borderRadius="lg" fontSize="sm" textAlign="center">
          {tokenError}
        </Box>
        <chakra.button
          bg="transparent"
          border="none"
          color="volyx.primary"
          fontSize="sm"
          fontWeight="semibold"
          cursor="pointer"
          p="0"
          transition="opacity 0.2s"
          _hover={{ opacity: 0.8 }}
          outline="none"
          onClick={() => navigate('/forgot-password')}
        >
          Request a new reset link
        </chakra.button>
      </Flex>
    );
  }

  const passwordErrors = getPasswordErrors(password);

  return (
    <>
      <chakra.form onSubmit={handleSubmit} w="full" maxW="400px" display="flex" flexDirection="column" alignItems="center">
        <Text as="h1" fontSize="xl" fontWeight="semibold" color="volyx.text" mb="2" textAlign="center">
          Set new password
        </Text>
        <Text fontSize="sm" color="volyx.textSecondary" mb="6" textAlign="center" lineHeight="normal">
          Choose a strong password for your account.
        </Text>

        <Flex w="full" direction="column" gap="4" mb="4">
          <Box w="full">
            <Input
              id="reset-password-new"
              name="password"
              type="password"
              placeholder="New password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); if (error) setError(null); }}
              autoComplete="new-password"
              showPasswordToggle
              showPassword={showPassword}
              onPasswordToggle={() => setShowPassword((v) => !v)}
              required
            />
            <PasswordValidation password={password} errors={passwordErrors} />
          </Box>
          <Input
            id="reset-password-confirm"
            name="confirmPassword"
            type="password"
            placeholder="Confirm new password"
            value={confirmPassword}
            onChange={(e) => { setConfirmPassword(e.target.value); if (error) setError(null); }}
            autoComplete="new-password"
            showPasswordToggle
            showPassword={showConfirmPassword}
            onPasswordToggle={() => setShowConfirmPassword((v) => !v)}
            required
          />
        </Flex>

        {error && (
          <Box w="full" p="3" bg="rgba(255,59,48,0.1)" color="volyx.error" borderRadius="lg" fontSize="sm" mb="4" textAlign="center">
            {error}
          </Box>
        )}

        <chakra.button
          w="full"
          px="6"
          py="3"
          borderRadius="xl"
          fontWeight="semibold"
          transition="all 0.3s"
          cursor="pointer"
          border="none"
          outline="none"
          bg="volyx.primary"
          color="white"
          boxShadow="md"
          _hover={{ boxShadow: 'lg', transform: 'translateY(-2px) scale(1.02)' }}
          _disabled={{ opacity: 0.7, cursor: 'not-allowed' }}
          disabled={loading}
        >
          {loading ? 'Updating...' : 'Update password'}
        </chakra.button>

        <chakra.button
          mt="6"
          bg="transparent"
          border="none"
          color="volyx.primary"
          fontSize="sm"
          fontWeight="semibold"
          cursor="pointer"
          p="0"
          transition="opacity 0.2s"
          _hover={{ opacity: 0.8 }}
          _focus={{ textDecoration: 'underline' }}
          outline="none"
          onClick={() => navigate('/login')}
        >
          Back to login
        </chakra.button>
      </chakra.form>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={hideToast}
        />
      )}
    </>
  );
};
